import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Filter } from "@gaming/xds-react-js-icons";

import { xboxOnboardSiteLocationPrefix } from "../../constants";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmPitchConceptStatus } from "../../models/crmStatus";
import { pitchListActions } from "../../redux/actions/pitchListActions";
import { getPitchListData, getPitchListIsLoading } from "../../redux/selectors/pitchListSelectors";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import {
  ConceptItem,
  ConceptsManager,
  ConceptsManagerConfig,
  conceptsManagerStyles as styles,
} from "../ConceptsManager";
import { SelectDropdown } from "../SelectDropdown/SelectDropdown";

type PitchStatusLabels = Readonly<{
  submitted: string;
  reviewed: string;
  draft: string;
}>;

type PitchStatusInfo = {
  id: string;
  label: string;
  dotClass: string;
  labelClass: string;
};

const getPitchStatusInfo = (
  status: CrmPitchConceptStatus | undefined,
  labels: PitchStatusLabels
): PitchStatusInfo => {
  switch (status) {
    case CrmPitchConceptStatus.Submitted:
      return {
        id: "submitted",
        label: labels.submitted,
        dotClass: styles.dotReview,
        labelClass: styles.statusReview,
      };
    case CrmPitchConceptStatus.Reviewed:
      return {
        id: "reviewed",
        label: labels.reviewed,
        dotClass: styles.dotApproved,
        labelClass: styles.statusApproved,
      };
    case CrmPitchConceptStatus.Unset:
    default:
      return {
        id: "draft",
        label: labels.draft,
        dotClass: styles.dotDraft,
        labelClass: styles.statusDraft,
      };
  }
};

export const PitchesManager: React.FC = () => {
  const [
    managerTitle,
    newPitchButton,
    searchPlaceholder,
    emptyStateTitle,
    emptyStateDescription,
    createButtonLabel,
    noPitchesFound,
    submittedLabel,
    reviewedLabel,
    draftLabel,
    filterAll,
    untitledPitch,
    filterAriaLabel,
    statusAriaLabel,
  ] = useStrings(
    "pitchesManagerTitle",
    "pitchesManagerNewPitchButton",
    "pitchesManagerSearchPlaceholder",
    "pitchesManagerEmptyStateTitle",
    "pitchesManagerEmptyStateDescription",
    "pitchesManagerCreateButton",
    "pitchesManagerNoPitchesFound",
    "submitted",
    "reviewed",
    "draft",
    "filterAll",
    "untitledPitch",
    "filterAriaLabel",
    "statusAriaLabel"
  );
  const pitchConfig: ConceptsManagerConfig = {
    title: managerTitle,
    createButtonLabel: newPitchButton,
    searchPlaceholder,
    emptyStateTitle,
    emptyStateDescription,
    emptyStateButtonLabel: createButtonLabel,
    noResultsMessage: noPitchesFound,
  };
  const statusLabels: PitchStatusLabels = {
    submitted: submittedLabel,
    reviewed: reviewedLabel,
    draft: draftLabel,
  };
  const statusFilterOptions = [
    { id: "all", label: filterAll },
    { id: "draft", label: draftLabel },
    { id: "submitted", label: submittedLabel },
    { id: "reviewed", label: reviewedLabel },
  ];
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector(getPitchListIsLoading);
  const listData = useSelector(getPitchListData);
  const canCreate = useSelector(getXboxOnboardData)?.hubPermissions.pitches.edit !== "none";
  const [searchBy, setSearchBy] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilterPopup, setShowFilterPopup] = useState(false);

  useEffect(() => {
    dispatch(pitchListActions.fetchPitchList.start());
  }, [dispatch]);

  const handleNewPitch = useCallback(() => {
    const newId = uuidv4();
    history.push(`${xboxOnboardSiteLocationPrefix}/pitch/${newId}`);
  }, [history]);

  const pitchConcepts = listData?.pitchConcepts
    ? Object.entries(listData.pitchConcepts)
    : undefined;

  const hasNeverCreated = !isLoading && (!pitchConcepts || pitchConcepts.length === 0);

  const items: ConceptItem[] | undefined = useMemo(() => {
    if (!pitchConcepts) {
      return undefined;
    }

    return pitchConcepts
      .map(([pitchId, rawData]) => {
        const data = rawData as {
          aboutYourGame?: { gameTitle?: string };
          state?: { status?: CrmPitchConceptStatus };
        };
        const gameTitle = data.aboutYourGame?.gameTitle || untitledPitch;
        const statusInfo = getPitchStatusInfo(data.state?.status, statusLabels);

        return {
          statusId: statusInfo.id,
          item: {
            id: pitchId,
            title: gameTitle,
            statusLabel: statusInfo.label,
            statusDotClass: statusInfo.dotClass,
            statusLabelClass: statusInfo.labelClass,
            datePrefix: "",
            formattedDate: null,
            onClick: () => {
              history.push(`${xboxOnboardSiteLocationPrefix}/pitch/${pitchId}`);
            },
          } as ConceptItem,
        };
      })
      .filter(({ item, statusId }) => {
        if (searchBy && !item.title.toLowerCase().includes(searchBy.toLowerCase())) {
          return false;
        }
        if (statusFilter !== "all" && statusId !== statusFilter) {
          return false;
        }
        return true;
      })
      .map(({ item }) => item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pitchConcepts, searchBy, statusFilter, history]);

  return (
    <ConceptsManager
      config={pitchConfig}
      items={items}
      isLoading={isLoading}
      hasNeverCreated={hasNeverCreated}
      onCreateNew={canCreate ? handleNewPitch : undefined}
      onSearchChange={setSearchBy}
      renderHeaderActions={() => (
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => setShowFilterPopup(!showFilterPopup)}
          aria-label={filterAriaLabel}
        >
          <Filter />
        </button>
      )}
      renderBelowHeader={() =>
        showFilterPopup ? (
          <div className={styles.filterPopup}>
            <div className={styles.filterDropdown}>
              <SelectDropdown
                aria-label={statusAriaLabel}
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusFilterOptions}
              />
            </div>
          </div>
        ) : null
      }
    />
  );
};
